/**
 * A dónde volver después de iniciar sesión (#1656).
 *
 * Un enlace de un correo —«Hay un documento para firmar» lleva a
 * `/portal/signatures?firmar=<id>`— se perdía si no había sesión: el guard
 * mandaba al login y, al entrar, todo terminaba en `/dashboard`. La persona
 * tenía que buscar el documento a mano, o creía que el enlace no funcionaba.
 *
 * Se guarda en `sessionStorage` y no en la URL porque el ingreso puede dar
 * varias vueltas (Google, SAML, el selector de empresa) y ninguna de ellas
 * conserva parámetros propios. El viaje a Google vuelve al MISMO subdominio
 * (el backend redirige al `origin` del que salió), así que el valor sigue ahí.
 * Si la persona elige otra empresa —otro subdominio— el destino no aplica y se
 * descarta.
 *
 * 🔴 **Solo rutas internas.** Lo que se guarda es una ruta del SPA (empieza con
 * `/` y no con `//`), y se navega con el router: nunca puede llevar a otro sitio.
 */

const CLAVE = 'zen-destino-tras-login'
/** Un destino de hace más de esto ya no es «lo que la persona estaba abriendo». */
const VIGENCIA_MS = 30 * 60 * 1000

/** Pantallas de ingreso: volver a ellas después de entrar no tiene sentido. */
const RUTAS_DE_INGRESO = [
  '/auth/',
  '/colaboradores/login',
  '/colaboradores/callback',
  '/colaboradores/activate',
  '/colaboradores/recuperar',
  '/colaboradores/restablecer',
  '/colaboradores/saml-error',
]

function esRutaInterna(ruta: string): boolean {
  return ruta.startsWith('/') && !ruta.startsWith('//') && !ruta.includes('\\')
}

/** Guarda la ruta que se estaba abriendo (con su `?query`), si vale la pena volver. */
export function recordarDestino(ruta: string): void {
  if (!esRutaInterna(ruta)) return
  if (ruta === '/' || ruta.startsWith('/dashboard')) return
  if (RUTAS_DE_INGRESO.some((p) => ruta.startsWith(p))) return
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify({ ruta, en: Date.now() }))
  } catch {
    /* sessionStorage no disponible: se entra al inicio, como antes */
  }
}

/**
 * ⚠️ Al entrar, DOS lugares piden el destino casi a la vez: el guard de
 * `/auth/login`, que se vuelve a dibujar apenas se guarda la sesión, y el
 * `onSuccess` del formulario. Si el primero lo consumía, el segundo mandaba al
 * dashboard (y en desarrollo React dibuja dos veces). Por eso, durante un
 * instante, quien lo pida de nuevo recibe el mismo valor.
 */
const REPETICION_MS = 3000
let recienTomado: { ruta: string; en: number } | null = null

/** Devuelve el destino pendiente y lo borra. `null` si no hay o venció. */
export function tomarDestino(): string | null {
  if (recienTomado && Date.now() - recienTomado.en < REPETICION_MS) return recienTomado.ruta
  recienTomado = null
  let crudo: string | null = null
  try {
    crudo = sessionStorage.getItem(CLAVE)
    sessionStorage.removeItem(CLAVE)
  } catch {
    return null
  }
  if (!crudo) return null
  try {
    const { ruta, en } = JSON.parse(crudo) as { ruta?: unknown; en?: unknown }
    if (typeof ruta !== 'string' || typeof en !== 'number') return null
    if (Date.now() - en > VIGENCIA_MS) return null
    if (!esRutaInterna(ruta)) return null
    recienTomado = { ruta, en: Date.now() }
    return ruta
  } catch {
    return null
  }
}

/** ¿Hay un destino pendiente? No lo consume. */
export function hayDestino(): boolean {
  if (recienTomado && Date.now() - recienTomado.en < REPETICION_MS) return true
  try {
    return sessionStorage.getItem(CLAVE) !== null
  } catch {
    return false
  }
}

export function descartarDestino(): void {
  recienTomado = null
  try {
    sessionStorage.removeItem(CLAVE)
  } catch {
    /* nada que descartar */
  }
}

/**
 * El portal tiene dos puertas a las mismas pantallas: `/colaboradores/…` (cuenta
 * propia) y `/portal/…` (entrando con el usuario del ERP). Si la persona abrió
 * una y resultó ser de la otra, se la lleva a la pantalla equivalente.
 */
export function enElPortal(ruta: string | null, puerta: 'portal' | 'colaboradores'): string {
  const inicio = `/${puerta}`
  if (!ruta) return inicio
  const m = ruta.match(/^\/(portal|colaboradores)(\/.*|\?.*)?$/)
  if (!m) return inicio
  return `${inicio}${m[2] ?? ''}`
}
